import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@eatclubapp/ec-component-library';
import { Modal } from '../../../Modal/Modal';
import { PrimaryButton } from '../../../button/PrimaryButton';
import { Heading } from '../../../Heading';
import { useMediaQuery, useTheme } from '@mui/material';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import MarketBusinessSVG from '../../../../assets/images/graphic_marketbusiness.svg';
import HeartChatSVG from '../../../../assets/icon_heart_chat.svg';
import StarNotificationSVG from '../../../../assets/icon_star_notification.svg';
import MailSVG from '../../../../assets/icon_mail.svg';
import FeaturedVenueSVG from '../../../../assets/icon_featured_venue.svg';
import useStyles, {
  getModalStyles,
  getModalInputStyles,
  getEnquireButtonStyles,
} from './MarketBusinessModalStyles';
import { getAppConfig } from '../../../../constants/appConfig';
import { formatCurrency } from '../../../../utils/helpers';
import { sendMarketingFeeEnquiryAction } from '../../../../actions/creditRebateAction';
import { restaurantActivePropTypes } from '../../../../data/models/Restaurant';

const MarketBusinessModal = ({
  isOpen,
  onClose,
  availableCredit = '0',
  sendMarketingFeeEnquiry,
  restaurantActive,
  onEnquirySent,
  sendEnquiryPending,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const breakpointMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const appConfig = getAppConfig();

  const restaurant = restaurantActive?.restaurant;
  const restId = restaurant?.objectId;

  const handleEnquire = async () => {
    if (!restId) {
      return;
    }

    const onCompletion = (data) => {
      // Close the current modal first, then show the success modal
      onClose();

      // Show the enquiry sent modal after closing the current modal
      setTimeout(() => {
        if (onEnquirySent) {
          onEnquirySent();
        }
      }, 300);
    };

    await sendMarketingFeeEnquiry(restId, onCompletion);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      hideFooter={true}
      inputStyle={getModalInputStyles(breakpointMdDown)}
      style={getModalStyles()}
    >
      <Box className={classes.marketModalContainer}>
        <Box className={classes.marketModalContent}>
          <Heading className={classes.marketModalTitle}>
            Market my business to millions of customers
          </Heading>
          <Box className={classes.marketModalDescription}>
            Expose your brand to over 2 million diners across Australia with EatClub. We'll leverage
            our marketing know-how to activate the right channels and reach customers at the exact
            moment they're making their dining decisions.
          </Box>

          <Box className={classes.marketModalSubheading}>
            Gain exposure across multiple channels:
          </Box>

          <Box className={classes.marketModalChannelsList}>
            <Box className={classes.marketModalChannelItem}>
              <HeartChatSVG
                className={`${classes.marketModalChannelIcon} ${classes.marketModalChannelIconHeartChat}`}
              />
              <Box className={classes.marketModalChannelText}>Social Media</Box>
            </Box>
            <Box className={classes.marketModalChannelItem}>
              <StarNotificationSVG
                className={`${classes.marketModalChannelIcon} ${classes.marketModalChannelIconStarNotification}`}
              />
              <Box className={classes.marketModalChannelText}>Real-time push notifications</Box>
            </Box>
            <Box className={classes.marketModalChannelItem}>
              <MailSVG
                className={`${classes.marketModalChannelIcon} ${classes.marketModalChannelIconMail}`}
              />
              <Box className={classes.marketModalChannelText}>Email Marketing</Box>
            </Box>
            <Box className={classes.marketModalChannelItem}>
              <FeaturedVenueSVG
                className={`${classes.marketModalChannelIcon} ${classes.marketModalChannelIconFeaturedVenue}`}
              />
              <Box className={classes.marketModalChannelText}>Featured Venue</Box>
            </Box>
          </Box>

          <Box className={classes.marketModalPricing}>
            <Box className={classes.marketModalPricingRow}>
              <Box className={classes.marketModalPricingLabel}>Starting from:</Box>
              <Box className={classes.marketModalPricingStrikethrough}>
                {formatCurrency(appConfig.marketingPrice, false, '-', false, true)}*
              </Box>
            </Box>
            <Box className={classes.marketModalPricingRow}>
              <Box className={classes.marketModalPricingSmallLabel}>
                Spend your available credit of:
              </Box>
              <Box className={classes.marketModalPricingAmount}>
                {formatCurrency(availableCredit, false, '-', false, true)}
              </Box>
            </Box>
          </Box>

          <Box className={classes.marketModalButtonContainer}>
            <PrimaryButton
              style={getEnquireButtonStyles()}
              onClick={handleEnquire}
              disabled={sendEnquiryPending}
            >
              {sendEnquiryPending ? 'Enquiring...' : 'Enquire'}
            </PrimaryButton>
          </Box>
        </Box>

        {!breakpointMdDown && (
          <Box className={classes.marketModalImageContainer}>
            <MarketBusinessSVG />
          </Box>
        )}
        <Box className={classes.marketModalDisclaimer}>*Based on pricing on a national average</Box>
      </Box>
    </Modal>
  );
};

MarketBusinessModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  availableCredit: PropTypes.string,
  startingPrice: PropTypes.string,
  sendMarketingFeeEnquiry: PropTypes.func.isRequired,
  restaurantActive: restaurantActivePropTypes.isRequired,
  onEnquirySent: PropTypes.func,
  sendEnquiryPending: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  restaurantActive: state.restaurantActive,
  sendEnquiryPending: state.creditRebate?.sendEnquiryPending || false,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      sendMarketingFeeEnquiry: sendMarketingFeeEnquiryAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(MarketBusinessModal);
